import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Calculator } from 'widgets/Calculator/Calculator';
import { CostCalculation } from 'widgets/CostCalculation/CostCalculation';

import { useKubeflexStore } from 'entities/kubeflex/model/useKubeflexStore';
import { apiService } from 'shared/api/apiService';
import { LoaderBlock } from 'shared/ui/Loader/LoaderBlock';
import styles from './HomePage.module.scss';

export const HomePage = () => {
	const elementRef = useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const [isOpen, setIsOpen] = useState(false);

	const methods = useForm({
		mode: `onChange`,
		defaultValues: {
			nodes: 1,
			nodesType: ``,
		},
	});

	const { data, isLoading } = useQuery({
		queryKey: [`getKubeflexConfigInfo`],
		queryFn: apiService.getKubeflexConfigInfo,
		retryDelay: 1000 * 2,
	});

	const { setConfiguration } = useKubeflexStore();

	useEffect(() => {
		if (data) {
			setConfiguration(data);
		}
	}, [data, setConfiguration]);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 768);
			if (window.innerWidth > 768) {
				setIsOpen(false);
			}
		};
		handleResize();
		window.addEventListener(`resize`, handleResize);

		return () => window.removeEventListener(`resize`, handleResize);
	}, [isOpen]);

	useEffect(() => {
		const handleScroll = () => {
			if (!isMobile) {
				setIsVisible(true);
			}
			if (elementRef.current) {
				const rect = elementRef.current.getBoundingClientRect();
				const windowHeight = window.innerHeight || document.documentElement.clientHeight;
				const windowWidth = window.innerWidth || document.documentElement.clientWidth;

				// Check if element is visible in viewport
				const elementIsVisible =
					rect.top < windowHeight && rect.bottom > 0 && rect.left < windowWidth && rect.right > 0;

				setIsVisible(elementIsVisible);
			}
		};

		window.addEventListener(`scroll`, handleScroll);
		window.addEventListener(`resize`, handleScroll);
		// Initial check on component mount
		handleScroll();

		return () => {
			window.removeEventListener(`scroll`, handleScroll);
			window.removeEventListener(`resize`, handleScroll);
		};
	}, [isMobile]);

	return (
		<LoaderBlock isLoading={isLoading}>
			<div ref={elementRef} className={styles.container}>
				<FormProvider {...methods}>
					<div className={styles.wrapper}>
						<Calculator />
						<div className={styles.calculation}>
							{isVisible && <CostCalculation isMobile={isMobile} isOpen={isOpen} setIsOpen={setIsOpen} />}
						</div>
					</div>
				</FormProvider>
			</div>
		</LoaderBlock>
	);
};
